import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card, PageHeader, Btn, Field, inputCls, StatusPill, Empty, CheckboxGroup } from "../../components/dash/Kit";
import { api, errMessage } from "../../lib/api";

const contentTypes = ["Instagram Post", "Story", "Reel Concept", "TikTok Concept", "Facebook Post", "LinkedIn Post", "Promotion", "Announcement", "Other"];
const ctas = ["Book now", "Reserve now", "Visit us", "Shop now", "Learn more", "Send us a message", "Call us", "Visit website"];
const empty = { content_type: "Instagram Post", purpose: "", description: "", target_date: "", cta: "", instructions: "", media_ids: [] };

export default function Requests() {
  const [form, setForm] = useState(empty);
  const [errors, setErrors] = useState({});
  const [requests, setRequests] = useState([]);
  const [revisions, setRevisions] = useState([]);
  const [media, setMedia] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/content-requests").then((r) => setRequests(r.data)).catch(() => {});
    api.get("/revisions").then((r) => setRevisions(r.data)).catch(() => {});
    api.get("/media").then((r) => setMedia(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const n = {};
    if (!form.description.trim()) n.description = "Please describe what you need.";
    setErrors(n);
    if (Object.keys(n).length) return;
    setBusy(true);
    try {
      await api.post("/content-requests", form);
      toast.success("Request submitted.");
      setForm(empty);
      load();
    } catch (err) {
      toast.error(errMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="requests-page">
      <PageHeader
        title="Request Additional Content"
        body="Need something extra this month? Tell us what you have in mind."
      />

      <Card testId="extra-fee-note" className="border-amber-200 bg-amber-50">
        <p className="text-sm text-amber-900">
          Additional content outside your package may require an additional fee. You will be informed before any extra
          charges are made.
        </p>
      </Card>

      <form onSubmit={submit} noValidate data-testid="content-request-form">
        <Card>
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Content Type">
              <select data-testid="request-type-select" className={inputCls} value={form.content_type} onChange={set("content_type")}>
                {contentTypes.map((c) => (<option key={c} value={c}>{c}</option>))}
              </select>
            </Field>
            <Field label="Target Date" htmlFor="req-date">
              <input id="req-date" type="date" data-testid="request-date-input" className={inputCls} value={form.target_date} onChange={set("target_date")} />
            </Field>
          </div>

          <div className="mt-5">
            <Field label="Purpose" htmlFor="req-purpose">
              <input id="req-purpose" data-testid="request-purpose-input" className={inputCls} value={form.purpose} onChange={set("purpose")} placeholder="Announce our new opening hours" />
            </Field>
          </div>

          <div className="mt-5">
            <Field label="Description *" error={errors.description} htmlFor="req-description">
              <textarea id="req-description" rows={4} data-testid="request-description-input" className={inputCls} value={form.description} onChange={set("description")} />
            </Field>
          </div>

          <div className="mt-5 grid gap-5 sm:grid-cols-2">
            <Field label="Call To Action">
              <select data-testid="request-cta-select" className={inputCls} value={form.cta} onChange={set("cta")}>
                <option value="">Choose a CTA</option>
                {ctas.map((c) => (<option key={c} value={c}>{c}</option>))}
              </select>
            </Field>
            <Field label="Special Instructions" htmlFor="req-instructions">
              <input id="req-instructions" data-testid="request-instructions-input" className={inputCls} value={form.instructions} onChange={set("instructions")} />
            </Field>
          </div>

          {media.length > 0 && (
            <div className="mt-5">
              <Field label="Attach uploaded media" hint="Upload new files on the Upload Media page first.">
                <CheckboxGroup
                  name="request-media"
                  columns={2}
                  options={media.slice(0, 10).map((m) => m.filename)}
                  value={form.media_ids.map((id) => media.find((m) => m.id === id)?.filename).filter(Boolean)}
                  onChange={(names) =>
                    setForm((f) => ({
                      ...f,
                      media_ids: media.filter((m) => names.includes(m.filename)).map((m) => m.id),
                    }))
                  }
                />
              </Field>
            </div>
          )}

          <Btn type="submit" testId="request-submit-button" className="mt-6" disabled={busy}>
            {busy ? "Submitting…" : "Submit Request"}
          </Btn>
        </Card>
      </form>

      <div>
        <p className="mb-3 font-display text-lg font-semibold text-slate-900">Your requests</p>
        {requests.length === 0 ? (
          <Empty testId="requests-empty" title="No requests yet" body="Extra content requests appear here." />
        ) : (
          <div className="space-y-3" data-testid="requests-list">
            {requests.map((r) => (
              <Card key={r.id} testId={`request-row-${r.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="font-display text-base font-semibold text-slate-900">{r.content_type}</p>
                    <p className="mt-1 text-sm text-slate-600">{r.description}</p>
                    <p className="mt-2 text-xs text-slate-500">{r.target_date || "No target date"}</p>
                  </div>
                  <StatusPill status={r.status} />
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      <div>
        <p className="mb-3 font-display text-lg font-semibold text-slate-900">Revision requests</p>
        {revisions.length === 0 ? (
          <Empty testId="revisions-empty" title="No revision requests" body="Request changes from the My Content page." />
        ) : (
          <div className="space-y-3" data-testid="revisions-list">
            {revisions.map((r) => (
              <Card key={r.id} testId={`revision-row-${r.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-slate-900">{r.areas.join(", ") || "Change requested"}</p>
                    <p className="mt-1 text-sm text-slate-600">{r.description}</p>
                    {r.admin_notes && <p className="mt-2 rounded-xl bg-slate-50 p-3 text-sm text-slate-600">PostMint: {r.admin_notes}</p>}
                  </div>
                  <StatusPill status={r.status} />
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
